/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Keyboard-shortcuts reference pane. A full, grouped guide covering the
// message composer (formatting + behaviour), autocomplete overlays, and
// app navigation. The modifier glyph adapts to the platform (⌘ on
// macOS, Ctrl elsewhere). Kept in sync with the composer's actual key
// handling in `@ping/enriched-text-input` + `ChatRoomComposer`.

import { Text, View } from "react-native";
import { SettingsRow } from "../SettingsRow";

// Platform modifier glyphs. macOS (and the desktop app) use the Apple
// symbols; web on Windows/Linux uses the spelled-out names.
const IS_MAC =
  typeof navigator === "undefined"
    ? true
    : /mac|iphone|ipad|ipod/i.test(
        // `platform` is the most reliable signal where present.
        (navigator as Navigator & { userAgentData?: { platform?: string } })
          .userAgentData?.platform ??
          navigator.platform ??
          navigator.userAgent ??
          "",
      );
const MOD = IS_MAC ? "⌘" : "Ctrl";
const ALT = IS_MAC ? "⌥" : "Alt";
const SHIFT = "⇧";

interface Shortcut {
  readonly label: string;
  /** Key tokens rendered as caps; nested arrays render as alternatives ("or"). */
  readonly keys: ReadonlyArray<ReadonlyArray<string>>;
}

interface Section {
  readonly title: string;
  readonly description?: string;
  readonly items: ReadonlyArray<Shortcut>;
  /** Stable anchor for the per-pane guide tour, when this section is featured. */
  readonly testID?: string;
}

const SECTIONS: ReadonlyArray<Section> = [
  {
    title: "Text formatting",
    testID: "settings-keyboard-text-formatting",
    description: "Works with the cursor in the message field, on a selection or to toggle as you type.",
    items: [
      { label: "Bold", keys: [[MOD, "B"]] },
      { label: "Italic", keys: [[MOD, "I"]] },
      { label: "Strikethrough", keys: [[MOD, SHIFT, "X"]] },
      { label: "Inline code", keys: [[MOD, SHIFT, "C"]] },
      { label: "Code block", keys: [[MOD, ALT, SHIFT, "C"]] },
      { label: "Blockquote", keys: [[MOD, SHIFT, "9"]] },
      { label: "Numbered list", keys: [[MOD, SHIFT, "7"]] },
      { label: "Bulleted list", keys: [[MOD, SHIFT, "8"]] },
      { label: "Link", keys: [[MOD, SHIFT, "U"], [MOD, "K"]] },
      { label: "Apply markdown to typed text", keys: [[MOD, SHIFT, "F"]] },
    ],
  },
  {
    title: "Lists & blocks",
    items: [
      { label: "Indent / nest list item", keys: [["Tab"]] },
      { label: "Outdent list item", keys: [[SHIFT, "Tab"]] },
      { label: "Exit list / code block", keys: [["Enter", "Enter"]] },
    ],
  },
  {
    title: "Composing & sending",
    testID: "settings-keyboard-composing",
    description: "Markup auto-formats as you type: *bold*, _italic_, ~strike~, `code`, ``` for a code block, > quote, - or 1. for lists. Emoticons like :) become emoji.",
    items: [
      { label: "Send message", keys: [["Enter"], [MOD, "Enter"]] },
      { label: "New line (no send)", keys: [[SHIFT, "Enter"]] },
      { label: "Edit your last message", keys: [["↑"]] },
      { label: "Mention someone", keys: [["@"]] },
      { label: "Insert emoji by name", keys: [[":"]] },
      { label: "Paste a link onto selected text", keys: [[MOD, "V"]] },
      { label: "Undo", keys: [[MOD, "Z"]] },
    ],
  },
  {
    title: "Autocomplete menu",
    testID: "settings-keyboard-autocomplete",
    description: "While the @ / : suggestion menu is open.",
    items: [
      { label: "Move selection", keys: [["↑"], ["↓"]] },
      { label: "Accept suggestion", keys: [["Enter"], ["Tab"]] },
      { label: "Dismiss menu", keys: [["Esc"]] },
    ],
  },
  {
    title: "Code block tiles",
    items: [
      { label: "Copy code", keys: [["Copy"]] },
      { label: "Expand / collapse long snippet", keys: [["View full snippet"]] },
    ],
  },
  {
    title: "Navigation",
    testID: "settings-keyboard-navigation",
    items: [
      { label: "Open settings", keys: [[MOD, ","]] },
      { label: "Conversation switcher", keys: [[MOD, "K"]] },
      { label: "Close dialog / menu", keys: [["Esc"]] },
    ],
  },
];

function Kbd({ label }: { readonly label: string }): JSX.Element {
  return (
    <View className="min-w-[22px] items-center justify-center rounded-[6px] px-[6px] py-[2px] bg-light-surface-secondary dark:bg-white/[0.08] border border-light-border-secondary dark:border-white/[0.08]">
      <Text className="font-sans text-[12px] font-semibold text-light-text-primary dark:text-[#E6E8EC]">
        {label}
      </Text>
    </View>
  );
}

function ShortcutRow({
  item,
  first,
}: {
  readonly item: Shortcut;
  readonly first: boolean;
}): JSX.Element {
  return (
    <View
      className={`flex flex-row items-center justify-between gap-3 py-[8px] ${
        first ? "" : "border-t border-light-border-secondary dark:border-white/[0.06]"
      }`}
    >
      <Text className="flex-1 font-sans text-[13px] text-light-text-primary dark:text-[#CDD2DA]">
        {item.label}
      </Text>
      <View className="flex flex-row items-center gap-[6px]">
        {item.keys.map((combo, ci) => (
          <View key={ci} className="flex flex-row items-center gap-[6px]">
            {ci > 0 ? (
              <Text className="font-sans text-[11px] text-light-text-tertiary dark:text-[#6B7280]">
                or
              </Text>
            ) : null}
            <View className="flex flex-row items-center gap-[3px]">
              {combo.map((k, ki) => (
                <Kbd key={ki} label={k} />
              ))}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

export function KeyboardShortcutsPane(): JSX.Element {
  return (
    <View className="flex flex-col gap-4">
      <SettingsRow
        variant="hero"
        title="Keyboard shortcuts"
        description="Everything you can do from the keyboard — formatting, composing, and getting around."
        testID="settings-keyboard-intro"
      />
      {SECTIONS.map((section) => (
        <View
          key={section.title}
          {...(section.testID !== undefined ? { testID: section.testID } : {})}
          className="flex flex-col gap-1"
        >
          <Text className="font-sans text-[13px] font-bold text-light-text-primary dark:text-white">
            {section.title}
          </Text>
          {section.description !== undefined ? (
            <Text className="font-sans text-[12px] leading-[16px] text-light-text-tertiary dark:text-[#8A90A0] mb-1">
              {section.description}
            </Text>
          ) : null}
          <View className="rounded-[12px] px-3 py-1 bg-light-surface-secondary dark:bg-white/[0.03]">
            {section.items.map((item, idx) => (
              <ShortcutRow
                key={item.label}
                item={item}
                first={idx === 0}
              />
            ))}
          </View>
        </View>
      ))}
    </View>
  );
}
